import { ContentEditable } from '@criteria-labs/content-editable'
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu } from '@criteria-labs/react'
import { EllipsisHorizontalIcon } from '@heroicons/react/16/solid'
import { ChevronDownIcon, DocumentIcon, FolderIcon } from '@heroicons/react/24/outline'
import {
  PencilIcon,
  DocumentIcon as SolidDocumentIcon,
  FolderIcon as SolidFolderIcon,
  TrashIcon
} from '@heroicons/react/24/solid'
import clsx from 'clsx'
import { useState } from 'react'
import { NodeRendererProps } from 'react-arborist'
import { ProjectItem } from '../ProjectItem'

const colors = {
  indigo: {
    active: 'bg-indigo-100 dark:bg-indigo-950'
  }
}

export function ProjectNavigatorItem({ node, style, dragHandle, tree }: NodeRendererProps<ProjectItem>) {
  const classes = clsx(
    // Base styles
    'group focus:outline-none py-0.5',

    // Text styles
    'text-left text-base/5 sm:text-sm/5 forced-colors:text-[CanvasText]',
    node.isSelected
      ? ['text-zinc-900 dark:text-white', colors['indigo'].active]
      : ['text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white'],

    // Use subgrid when available but fallback to an explicit grid layout if not
    'grid grid-cols-[1.5rem_1.5rem_1fr_auto] items-center'
  )
  const Icon =
    node.data.store === 'userFileSystem'
      ? node.isLeaf
        ? SolidDocumentIcon
        : SolidFolderIcon
      : node.isLeaf
      ? DocumentIcon
      : FolderIcon
  const [editedName, setEditedName] = useState(node.data.name)

  const canRename = node.data.store === 'originPrivateFileSystem'
  const canDelete = node.data.store === 'originPrivateFileSystem' || !node.data.path.slice(1).includes('/')

  return (
    <div
      style={style}
      ref={dragHandle}
      className={classes}
      onDoubleClick={() => {
        node.edit()
      }}
    >
      {!node.isLeaf && (
        <button type="button" onClick={() => node.toggle()}>
          <ChevronDownIcon className={clsx('ml-1 size-4 transform', node.isOpen ? '' : '-rotate-90')} />
        </button>
      )}
      <Icon
        className={clsx(
          'size-4',
          node.isLeaf ? 'col-start-2 row-start-1' : '',
          node.data.store === 'userFileSystem' ? 'text-blue-500' : ''
        )}
      />
      <div className="col-start-3 row-start-1 truncate">{node.data.name}</div>
      {node.isEditing && (
        <ContentEditable
          data-id={`${node.data.path}-name`}
          className="absolute inset-0 flex items-center bg-white px-1 dark:bg-zinc-950"
          value={editedName}
          sanitize={(name) => name.replaceAll('/', '')}
          placeholder={
            <div className="pointer-events-none absolute absolute inset-0 top-0 flex items-center px-1 text-zinc-500">
              Enter a name&hellip;
            </div>
          }
          onChange={setEditedName}
          autoFocus={true}
          onFocus={(event) => {
            // select all on focus
            const element = event.target
            var range = document.createRange()
            range.selectNodeContents(element)
            var selection = window.getSelection()
            selection?.removeAllRanges()
            selection?.addRange(range)
          }}
          onBlur={async () => {
            if (editedName.length === 0 || editedName === node.data.name) {
              node.reset()
              setEditedName(node.data.name)
            } else {
              const newName = editedName.replaceAll('/', '').trim()

              node.submit(editedName.replaceAll('/', '').trim())

              setEditedName(newName)
            }
          }}
        />
      )}
      {(canRename || canDelete) && (
        <Dropdown>
          {({ open }) => (
            <>
              <DropdownButton
                as="button"
                aria-label="More options"
                className={clsx(
                  'col-start-4 row-start-1 mx-1 size-4 text-zinc-500',
                  open ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                )}
                onClick={(event) => {
                  event.stopPropagation()
                }}
              >
                <EllipsisHorizontalIcon className="h-3 w-3" />
              </DropdownButton>
              <DropdownMenu>
                {canRename && (
                  <DropdownItem
                    onClick={() => {
                      node.edit()
                    }}
                  >
                    <PencilIcon />
                    <DropdownLabel>Rename</DropdownLabel>
                  </DropdownItem>
                )}
                {canDelete && (
                  <DropdownItem
                    disabled={!canDelete}
                    onClick={() => {
                      tree.delete(node)
                      tree.deselectAll()
                    }}
                  >
                    <TrashIcon />
                    <DropdownLabel>{node.data.store === 'originPrivateFileSystem' ? 'Delete' : 'Remove'}</DropdownLabel>
                  </DropdownItem>
                )}
              </DropdownMenu>
            </>
          )}
        </Dropdown>
      )}
    </div>
  )
}
